import React, { useState, useEffect, useContext, useMemo } from 'react';
import withAppContext from 'tbk-components/src/hooks/withAppContext';
import useHashAsState from 'tbk-components/src/hooks/useHashAsState';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Image, { IImage } from 'tbk-components/src/components/Image';
import DonationFormButtons from './DonationFormButtons';
import DonorToolPaymentTypeInfo, {IPaymentTypeInfo} from './DonorToolPaymentTypeInfo';
import Autocomplete from 'react-autocomplete';
import { ICartItem } from './DonorToolCart';
import Loading from 'tbk-components/src/components/Loading';
import {handleGtmTags} from "../../GTMHandler";

export interface IDonationFormProps extends IBaseElementProps {
	children?: any;
	hideProgramInformation?: boolean;
	hideInitialLoading?: boolean;
	donationFormIntroHeading?: string;
	donationFormIntroText?: string;
	programInformation?: any;
	initialItem?: ICartItem;
	paymentTypeInfo?: IPaymentTypeInfo;
	viewMode?: string;
}

const DonationForm: React.FC<IDonationFormProps> = withAppContext(
	({
		 context,
		 children,
		 className,
		 classNames = [],
		 hideProgramInformation = false,
		 hideInitialLoading = false,
		 donationFormIntroHeading,
		 donationFormIntroText,
		 programInformation,
		 initialItem = { donationType: 'one-time' } as ICartItem,
		 paymentTypeInfo,
		 viewMode,
	 }) => {
		const [programCodeInitialState] = useHashAsState({
			parameters: {
				code: 'string',
			},
			defaults: {},
		});

		const initialDonation = { ...initialItem };

		if (initialDonation.donationRecurringDuration == 'one-time') {
			delete initialDonation.donationRecurringDuration;
		}

		if (programCodeInitialState.code) {
			initialDonation.programCode =
				programCodeInitialState.code.toUpperCase();
		}

		const { Api } = useContext(context);
		const [projectsForSearch, setProjectsForSearch] = useState([]);
		const [donation, setDonation] = useState(initialDonation);
		const [busy, setBusy] = useState(false);
		const [searchBusy, setSearchBusy] = useState(false);
		const [mySelectedProgram, setMySelectedProgram] = useState(null);
		const [searchVal, setSearchVal] = useState(
			initialDonation?.programCode || '',
		);
		const [errorMessage, setErrorMessage] = useState('');
		const [autocompleteOpen, setAutocompleteOpen] = useState(true);
		const [resultsFound, setResultsFound] = useState<boolean>(true);

		useEffect(() => {
			if (programInformation) {
				setMySelectedProgram(programInformation);
				setDonation((donation) => ({
					...donation,
					programCode: programInformation.programCode,
				}));
				return;
			}
			setSearchBusy(true);
			Api.getProjectsForSearch([searchVal])
				.then((projectsForSearch) => {
					// loop through the search results and see if it matches the value perfectly
					let code_id_found = 0;
					for (let i = 0; i < projectsForSearch.length; i++) {
						if (projectsForSearch[i].label == searchVal) {
							code_id_found = projectsForSearch[i].value;
							break;
						}
					}
					// Only show once a donation code has been found:
					if (searchVal.length > 6 && code_id_found > 0) {
						// get the program information
						return Api.getProgramInformation([searchVal]).then(
							(programInformation) => ({
								programInformation,
								projectsForSearch: null,
							}),
						);
					}

					return {
						projectsForSearch,
						programInformation: null,
					};
				})
				.then(({ projectsForSearch, programInformation }) => {
					setProjectsForSearch(projectsForSearch);
					setMySelectedProgram(programInformation);
					// if there are no values in the dropdown to choose from and the user entered something, no results found.
					if (
						(projectsForSearch?.length == 0 ||
							projectsForSearch == null) &&
						searchVal.length > 0
					) {
						setResultsFound(false);
					}
					if (programInformation) {
						setResultsFound(true);
						setDonation((donation) => ({
							...donation,
							programCode: programInformation.programCode,
						}));
					}
				})
				.finally(() => {
					setSearchBusy(false);
				});
		}, [searchVal, programInformation]);

		const handleSubmit = () => {
			setBusy(true);

			Api.addEditDonation(donation)
				.then((results) => {
					handleGtmTags(results);
					//redirect to cart page
					window.location.href = results.url;
				})
				.catch((message) => {
					setErrorMessage(message);
					setBusy(false);
				});
		};

		const autocompleteInputProps = {
			id: 'project_code_search_entry',
			className: 'form-control',
			placeholder: 'XXXXXXXX',
			'aria-label': 'Donation Code Search',
		};

		const autocompleteOnChange = (event, value) => {
			setResultsFound(true);
			setMySelectedProgram(null);
			setSearchVal(value.toUpperCase());
		};

		const autocompleteOnSelect = (event, returnValue) => {
			autocompleteOnChange(event, returnValue.label);
			if (returnValue.label.length == 6) {
				setAutocompleteOpen(true);
			}
		};

		useEffect(() => {
			if (!projectsForSearch || projectsForSearch.length === 1) {
				setAutocompleteOpen(false);
			} else {
				setAutocompleteOpen(true);
			}
		}, [projectsForSearch]);

		// if there is an initialItem.programCode, that means the user has already selected a program
		// whether it be via edit or if the system provided a program code for them.
		let donationButtonText = 'Donate Now';
		if (initialItem?.programCode && initialItem?.donationAmount > 0) {
			donationButtonText = 'Update Donation';
		}

		if ('miniMode' == viewMode) {
			return (
				<li className="location-list__results-item">
					{mySelectedProgram?.programImage && (
						<div className="location-list__results-image">
							<Image
								className="img-fluid"
								{...mySelectedProgram.programImage}
							/>
						</div>
					)}
					<div className="location-list__results-content">
						{mySelectedProgram && (
							<div className="location-list__results-text">
								<div className="location-list__results-code">
									{mySelectedProgram.programCode}
								</div>
								<h3 className="location-list__results-heading">
									{mySelectedProgram.programTitle}
								</h3>
								<div
									className="location-list__results-subheading"
									dangerouslySetInnerHTML={{
										__html:
											mySelectedProgram.projectTitle +
											' ' +
											mySelectedProgram.projectLocation,
									}}
								></div>
								<p
									className="location-list__results-description"
									dangerouslySetInnerHTML={{
										__html: mySelectedProgram.programDescription,
									}}
								></p>
							</div>
						)}
						<div className="location-list__results-form">
							<fieldset className="location-list__results-fieldset">
								<legend className="visually-hidden">
									Frequency
								</legend>
								<div className="location-list__radio location-list__radio--frequency">
									{Object.entries({
										'One Time': 'one-time',
										Recurring: 'recurring',
									}).map((entry, index) => {
										const [label, value] = entry;
										return (
											<button
												key={index}
												className={`btn ${
													donation.donationType ===
													value
														? 'active'
														: ''
												}`}
												onClick={() =>
													setDonation((donation) => ({
														...donation,
														donationType: value,
													}))
												}
											>
												{label}
											</button>
										);
									})}
								</div>
							</fieldset>
							{donation.donationType == 'recurring' && (
								<fieldset className="location-list__results-fieldset location-list__results-fieldset--timeframe">
									<legend className="visually-hidden">
										Time Frame
									</legend>
									<div className="location-list__radio location-list__radio--timeframe">
										{Object.entries({
											Monthly: 'monthly',
											Quarterly: 'quarterly',
											'Semi-Annually': 'semi-annually',
											Annually: 'annually',
										}).map((entry, index) => {
											const [label, value] = entry;
											return (
												<button
													key={index}
													className={`btn ${
														donation.donationRecurringDuration ===
														value
															? 'active'
															: ''
													}`}
													onClick={() =>
														setDonation(
															(donation) => ({
																...donation,
																donationRecurringDuration:
																value,
															}),
														)
													}
												>
													{label}
												</button>
											);
										})}
									</div>
								</fieldset>
							)}
							<div className="donation-amounts-container">
								<DonationFormButtons
									viewMode="miniMode"
									value={donation.donationAmount}
									disabled={
										donation.donationType !== 'one-time' &&
										!donation.donationRecurringDuration
									}
									onChange={(amount) =>
										setDonation((donation) => ({
											...donation,
											donationAmount: amount,
										}))
									}
								/>
								<div className="donation-submit">
									<button
										className="donation-submit__btn btn btn-lg btn-dark-2"
										disabled={
											!(
												donation.donationAmount > 0 &&
												(donation.donationType ==
													'one-time' ||
													(donation.donationType ==
														'recurring' &&
														donation.donationRecurringDuration !==
														undefined)) &&
												!busy
											)
										}
										onClick={handleSubmit}
									>
										{donationButtonText}
									</button>
									{busy && <Loading />}
								</div>
							</div>
						</div>
					</div>
				</li>
			);
		} else {
			return (
				<div className="donation-form-outer-container container-fluid">
					<div
						className={
							className || cn('donation-form', ...classNames)
						}
					>
						{(donationFormIntroHeading ||
							donationFormIntroText) && (
							<div className="donation-form-intro-content">
								{donationFormIntroHeading && (
									<h2 className="donation-form-intro-title">
										{donationFormIntroHeading}
									</h2>
								)}
								{donationFormIntroText && (
									<p className="donation-form-intro-text">
										{donationFormIntroText}
									</p>
								)}
							</div>
						)}
						{mySelectedProgram === null && !hideInitialLoading && (
							<Loading />
						)}
						<div className="donation-form-code-search-container">
							{!initialItem?.programCode && (
								<>
									{errorMessage && (
										<div className="error-message">
											{errorMessage}
										</div>
									)}
									<div className="input-group">
										<span className="btn">Code</span>
										<div className="autocomplete-wrapper">
											<Autocomplete
												value={searchVal}
												items={projectsForSearch || []}
												getItemValue={(item) =>
													item.label
												}
												onChange={autocompleteOnChange}
												onSelect={autocompleteOnSelect}
												open={autocompleteOpen}
												inputProps={
													autocompleteInputProps
												}
												renderMenu={(children) => (
													<div className="menu">
														{children}
													</div>
												)}
												renderItem={(
													item,
													isHighlighted,
												) => (
													<div
														className={`item ${
															isHighlighted
																? 'item-highlighted'
																: ''
														}`}
														key={item.ID}
														tabIndex={0}
													>
														{item.label}
													</div>
												)}
											/>
										</div>
										{searchBusy && (
											<Loading inline={true} />
										)}
									</div>
								</>
							)}
							{mySelectedProgram && !hideProgramInformation && (
								<div className="donation-form-search-results">
									<div className="donation-form-search-results__item">
										<div className="donation-form-search-results__item-image">
											{mySelectedProgram.programImage && (
												<Image
													className="img-fluid"
													{...mySelectedProgram.programImage}
												/>
											)}
										</div>
										<div className="donation-form-search-results__item-content">
											<p className="donation-form-search-results__item-code">
												{mySelectedProgram.programCode}
											</p>
											<h3
												className="donation-form-search-results__item-title"
												dangerouslySetInnerHTML={{
													__html: mySelectedProgram.programTitle,
												}}
											></h3>
											<p
												className="donation-form-search-results__item-info"
												dangerouslySetInnerHTML={{
													__html:
														mySelectedProgram.projectTitle +
														' ' +
														mySelectedProgram.projectLocation,
												}}
											></p>
											<p
												className="donation-form-search-results__item-text"
												dangerouslySetInnerHTML={{
													__html: mySelectedProgram.programDescription,
												}}
											></p>
										</div>
									</div>
								</div>
							)}
							{!resultsFound && !hideProgramInformation && (
								<div className="donation-form-search-results">
									There are no donations under this donation
									code. Please try again
								</div>
							)}
						</div>
						{mySelectedProgram && (
							<div className={'donation-form-container'}>
								<nav>
									<div
										className="btn-group nav nav-tabs"
										role="group"
										aria-label="Donation Amount Selection"
									>
										{Object.entries({
											'One Time': 'one-time',
											Recurring: 'recurring',
										}).map((entry, index) => {
											const [label, value] = entry;
											return (
												<button
													key={index}
													className={`btn ${
														donation.donationType ===
														value
															? 'active'
															: ''
													}`}
													onClick={() =>
														setDonation(
															(donation) => ({
																...donation,
																donationType:
																value,
															}),
														)
													}
												>
													{label}
												</button>
											);
										})}
									</div>
								</nav>
								{donation.donationType == 'recurring' && (
									<div className="recurring-renewal-container">
										<div
											className="btn-group"
											role="group"
											aria-label="Recurring Renewal Selection"
										>
											{Object.entries({
												Monthly: 'monthly',
												Quarterly: 'quarterly',
												'Semi-Annually':
													'semi-annually',
												Annually: 'annually',
											}).map((entry, index) => {
												const [label, value] = entry;
												return (
													<button
														key={index}
														className={`btn ${
															donation.donationRecurringDuration ===
															value
																? 'active'
																: ''
														}`}
														onClick={() =>
															setDonation(
																(donation) => ({
																	...donation,
																	donationRecurringDuration:
																	value,
																}),
															)
														}
													>
														{label}
													</button>
												);
											})}
										</div>
									</div>
								)}
								<div className="donation-amounts-container">
									<DonationFormButtons
										value={donation.donationAmount}
										disabled={
											donation.donationType !==
											'one-time' &&
											!donation.donationRecurringDuration
										}
										onChange={(amount) =>
											setDonation((donation) => ({
												...donation,
												donationAmount: amount,
											}))
										}
									/>
								</div>
								<div className="donation-message">
									<button
										type="button"
										className="donation-message__btn btn"
										data-bs-toggle="collapse"
										data-bs-target={
											'#donation-message-collapse-' +
											'donation-form-tabs-'
										}
										aria-expanded="false"
										aria-controls={
											'donation-message-collapse-' +
											'donation-form-tabs-'
										}
									>
										Add a Message{' '}
										<span className="donation-message__btn-icon">
											<i className="icon icon-plus"></i>
										</span>
									</button>
									<div
										id={
											'donation-message-collapse-' +
											'donation-form-tabs-'
										}
										className="donation-message__input-container collapse"
									>
										<label
											htmlFor="donation-message__input"
											className="visually-hidden"
										>
											Donation Message
										</label>
										<textarea
											id="donation-message__input"
											className="donation-message__input form-control"
											placeholder="Add a personal note or more information on where you'd like to focus your donation..."
											rows={4}
											onChange={(e) => {
												donation.donationMessage =
													e.target.value;
												setDonation(donation);
											}}
										>
											{initialItem?.donationMessage}
										</textarea>
									</div>
									<div className="donation-submit">
										<button
											className="donation-submit__btn btn btn-lg btn-dark-2"
											disabled={
												!(
													donation.donationAmount >
													0 &&
													(donation.donationType ==
														'one-time' ||
														(donation.donationType ==
															'recurring' &&
															donation.donationRecurringDuration !==
															undefined)) &&
													!busy
												)
											}
											onClick={handleSubmit}
										>
											{donationButtonText}
										</button>
										{busy && (
											<div className="py-4">
												<Loading />
											</div>
										)}
									</div>
								</div>
								<div className="moneris-info-container">
									<DonorToolPaymentTypeInfo
										paymentTypeInfo={paymentTypeInfo}
									/>
								</div>
							</div>
						)}
					</div>
				</div>
			);
		}
	},
);
export default DonationForm;
